import { motion } from "framer-motion";
import { PenTool, HardHat, CheckCircle2, Wrench } from "lucide-react";
import CornerMarks from "@/components/site/CornerMarks";

const STEPS = [
  {
    n: "01",
    icon: PenTool,
    title: "Design",
    desc: "Loads, layouts, single-line diagrams, BOQs and energy modelling tailored to your site.",
  },
  {
    n: "02",
    icon: HardHat,
    title: "Build",
    desc: "Procurement, fabrication and on-site installation by licensed engineers and certified crews.",
  },
  {
    n: "03",
    icon: CheckCircle2,
    title: "Commission",
    desc: "Testing, calibration, performance verification and hand-over with full documentation.",
  },
  {
    n: "04",
    icon: Wrench,
    title: "Maintain",
    desc: "Telemetry-led O&M, remote monitoring and quick-response service to keep systems performing.",
  },
];

const Process = () => {
  return (
    <section
      id="process"
      className="relative py-20 sm:py-24 lg:py-28 overflow-hidden"
      style={{ background: "var(--bg-soft)" }}
      data-testid="process-section"
      aria-label="Our engineering process"
    >
      <div className="absolute inset-0 energy-grid-bg opacity-25 pointer-events-none" />
      <span
        aria-hidden
        className="absolute top-0 left-0 right-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, var(--accent-blue), transparent)",
          opacity: 0.4,
        }}
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-12">
          <motion.div
            initial={{ y: 18, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <span className="eyebrow">Our engineering process</span>
            <h2
              className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight font-light leading-[1.1]"
              style={{ color: "var(--text)" }}
            >
              From single-line diagram to{" "}
              <span className="font-medium">long-term performance.</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ y: 18, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-5 text-sm md:text-[15px] leading-relaxed"
            style={{ color: "var(--text-muted)" }}
          >
            Accountable engineering, start to finish.
          </motion.p>
        </div>

        <div className="relative">
          {/* Connecting line on desktop */}
          <span
            aria-hidden
            className="hidden lg:block absolute left-0 right-0 top-7 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent 4%, var(--accent-blue) 8%, var(--accent-crimson-hover) 92%, transparent 96%)",
              opacity: 0.45,
            }}
          />

          <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 lg:gap-8 relative">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.li
                  key={s.n}
                  initial={{ y: 24, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="relative"
                  data-testid={`process-step-${i}`}
                >
                  <div
                    className="relative w-14 h-14 flex items-center justify-center btn-sharp mb-5 backdrop-blur-xl"
                    style={{
                      background: "var(--surface-elev)",
                      border: "1px solid var(--accent-blue)",
                      color: "var(--accent-blue)",
                      boxShadow: "0 0 24px var(--accent-blue-glow)",
                    }}
                  >
                    <Icon className="w-5 h-5" strokeWidth={1.6} />
                    <span
                      className="absolute -top-2 -right-2 font-mono-spec text-[10px] tracking-[0.2em] uppercase px-1.5 py-0.5 btn-sharp"
                      style={{
                        background: "var(--accent-crimson)",
                        color: "#fff",
                      }}
                    >
                      {s.n}
                    </span>
                  </div>
                  <h3
                    className="font-display text-lg lg:text-xl font-medium"
                    style={{ color: "var(--text)" }}
                  >
                    {s.title}
                  </h3>
                  <p
                    className="mt-2 text-[14px] leading-relaxed"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {s.desc}
                  </p>
                </motion.li>
              );
            })}
          </ol>
        </div>
      </div>
      <CornerMarks code="SEC—02.PROCESS" />
    </section>
  );
};

export default Process;
